import { css, html, LitElement } from 'lit';

class CameraPreview extends LitElement {
  static properties = {
    onCapture: { type: Function },
    onClose: { type: Function },
    mode: { type: String }, // 'photo' or 'video'
    isRecording: { type: Boolean }
  };

  constructor() {
    super();
    this.mode = 'photo';
    this.isRecording = false;
    this.stream = null;
    this.mediaRecorder = null;
    this.recordedChunks = [];
  }

  static styles = css`
    :host {
      display: block;
      height: calc(100dvh - 75px);
      background-color: var(--bg-primary-color);    
      }

    .camera-container {
      position: relative;
      height: calc(100vh - 220px);
      display: flex;
      flex-direction: column;
    }

    .video-preview {
     flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      height: calc(100vh - 500px);
      position: relative;
      overflow: hidden;
      border-radius: 8px;
      transform: scaleX(-1);
    }

    .controls {
      position: absolute;
      bottom: -50px;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      gap: 20px;
      padding: 10px;
    }

    .control-button {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      border: none;
      background: var(--base-color);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .capture-button {
      width: 70px;
      height: 70px;
      border: 4px solid #fff;
    }


        .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 4px;
      border-radius: 8px;
      margin-bottom: 8px;
    }
       .tool-btn {
      background: none;
      border: none;
      color: var(--text-outgoing-message);
      font-size: 25px;
      cursor: pointer;
      opacity: 0.8;
      transition: opacity 0.2s;
      font-weight:bold;
      padding: 4px;
    }
        @media (max-width: 768px) {
        .video-preview {
     flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      height: calc(100vh - 100px);
      position: relative;
      overflow: hidden;
      border-radius: 8px;
      transform: scaleX(-1);
    }
        }
  `;

  async firstUpdated() {
    await this.startCamera();
  }

  async startCamera() {
    try {
      const constraints = {
        video: {
          facingMode: 'user'
        },
        audio: this.mode === 'video'
      };

      this.stream = await navigator.mediaDevices.getUserMedia(constraints);  // Fixed this line
      const video = this.shadowRoot.querySelector('video');
      video.srcObject = this.stream;
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  }


  async switchMode() {
    this.mode = this.mode === 'photo' ? 'video' : 'photo';
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    await this.startCamera();
  }

  async capturePhoto() {
    const video = this.shadowRoot.querySelector('video');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    
    canvas.toBlob(blob => {
      const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
      this.onCapture({
        file,
        type: 'image',
        src: canvas.toDataURL('image/jpeg')
      });
    }, 'image/jpeg');
  }

  startRecording() {
    this.isRecording = true;
    this.recordedChunks = [];
    this.mediaRecorder = new MediaRecorder(this.stream);
    
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      const blob = new Blob(this.recordedChunks, { type: 'video/webm' });
      const file = new File([blob], 'camera-recording.webm', { type: 'video/webm' });
      this.onCapture({
        file,
        type: 'video',
        src: URL.createObjectURL(blob)
      });
    };

    this.mediaRecorder.start();
  }

  stopRecording() {
    this.isRecording = false;
    this.mediaRecorder.stop();
  }

  handleCaptureClick() {
    if (this.mode === 'photo') {
      this.capturePhoto();
    } else {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        this.startRecording();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
  }
  renderToolbar() {
    return html`
      <div class="toolbar">
        <button class="tool-btn" @click="${this.onClose}">✕</button>
      </div>
    `;
  }
  render() {
    return html`
      <div class="camera-container">
      ${this.renderToolbar()}
        <video class="video-preview" autoplay playsinline></video>
        </button>
        
        <div class="controls">
          <button 
            class="control-button capture-button ${this.isRecording ? 'recording' : ''}"
            @click="${this.handleCaptureClick}"
          >
          <svg xmlns="http://www.w3.org/2000/svg" height="40px" viewBox="0 -960 960 960" width="40px" fill="#e8eaed"><path d="M479.67-264.67q73.33 0 123.5-50.16 50.16-50.17 50.16-123.5 0-73.34-50.16-123.17-50.17-49.83-123.5-49.83-73.34 0-123.17 49.83t-49.83 123.17q0 73.33 49.83 123.5 49.83 50.16 123.17 50.16Zm0-66.66q-45.67 0-76-30.67-30.34-30.67-30.34-76.33 0-45.67 30.34-76 30.33-30.34 76-30.34 45.66 0 76.33 30.34 30.67 30.33 30.67 76 0 45.66-30.67 76.33t-76.33 30.67ZM146.67-120q-27 0-46.84-19.83Q80-159.67 80-186.67v-502q0-26.33 19.83-46.5 19.84-20.16 46.84-20.16h140L360-840h240l73.33 84.67h140q26.34 0 46.5 20.16Q880-715 880-688.67v502q0 27-20.17 46.84Q839.67-120 813.33-120H146.67Zm0-66.67h666.66v-502H642.67l-73-84.66H390.33l-73 84.66H146.67v502ZM480-438Z"/></svg>
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('camera-preview', CameraPreview);